import { Inject, Injectable, OnDestroy, PLATFORM_ID } from "@angular/core";
import { BehaviorSubject, filter, Observable, ReplaySubject, Subject } from "rxjs";
import { isPlatformBrowser } from '@angular/common';
import { webSocket, WebSocketSubject } from 'rxjs/webSocket';
import Swal from "sweetalert2";

export enum WebSocketStatus {
    CONNECTING = 'CONNECTING',
    CONNECTED = 'CONNECTED',
    DISCONNECTED = 'DISCONNECTED',
    RECONNECTING = 'RECONNECTING',
    ERROR = 'ERROR'
}

export interface WebSocketMessage {
    type: string;
    message?: any;
    timestamp?: string;
    [key: string]: any;
}

@Injectable({
    providedIn: "root",
})

export class WebSocketsService implements OnDestroy {
    private socket: WebSocket | null = null;
    private reconnectionAttempts: number = 0;
    private maxAllowReconnectionsAttempts: number = 5;
    private reconnectionDelay: number = 2000;
    private heartBeatTimer: any;
    private heartBeatIntervalTime: number = 30000; // 30 seconds
    private messageSubject = new BehaviorSubject<WebSocketMessage>({} as WebSocketMessage);
    message$ = this.messageSubject.asObservable();

    constructor(@Inject(PLATFORM_ID) private platformId: Object) { }

    connect(url: string): void {

        if (!isPlatformBrowser(this.platformId)) {
            console.warn('WebSocket not initialized on server.');
            return;
        }

        if (this.isConnected()) return

        try {
            this.socket = new WebSocket(url);
            this.setupEventHandlers();
        }
        catch (error) {
            console.log('WebSocket connection error:', error);
            Swal.fire({
                icon: 'error',
                title: 'WebSocket Connection Error',
                text: error instanceof Error ? error?.message : 'An unknown error occurred.',
                timer: 5000,
                showConfirmButton: false,
                toast: true,
                position: "top-end",
            });
            this.attemptReconnection(url);
        }
    }

    setupEventHandlers(): void {
        if (!this.socket) return;

        this.socket.onopen = (event: Event) => {
            console.log('WebSocket connection opened.', event);
            this.reconnectionAttempts = 0;
            this.startHeartBeat();
        }

        this.socket.onmessage = (event: MessageEvent) => {
            try {

                const message: WebSocketMessage = JSON.parse(event.data);
                this.messageSubject.next(message);
                console.log('Message is JSON,:', message)
                if (message.type === 'pong') {

                }
            }
            catch (error) {
                console.warn('Message is not JSON, passing raw string:', event.data);
                console.log('Message is not JSON, passing raw string:', event.data)
                this.messageSubject.next({ type: 'raw', data: event.data });
            }
        }

        this.socket.onerror = (event: Event) => {
            console.log('WebSocket connection failed.', event);
            this.stopHeartBeat();
            this.attemptReconnection(this.socket?.url || '');
        }

        this.socket.onclose = (event: CloseEvent) => {
            console.log('WebSocket disconnected', event.code, event.reason);
            this.stopHeartBeat();
            // this.statusSubject.next(WebSocketStatus.DISCONNECTED);

            // Attempt reconnection if not manually closed
            if (event.code !== 1000) {
                this.attemptReconnection(this.socket?.url || '');
            }
        }
    }

    attemptReconnection(url: string): void {
        if (this.reconnectionAttempts > this.maxAllowReconnectionsAttempts) return;

        this.reconnectionAttempts++;
        setTimeout(() => {
            this.connect(url);
        }, this.reconnectionDelay * this.reconnectionAttempts);
    }

    startHeartBeat(): void {
        this.stopHeartBeat();

        this.heartBeatTimer = setInterval(() => {
            if (this.isConnected()) {
                this.send(JSON.stringify({ type: 'ping' }));
            }
        }, this.heartBeatIntervalTime);
    }

    stopHeartBeat(): void {
        if (this.heartBeatTimer) {
            clearInterval(this.heartBeatTimer);
            this.heartBeatTimer = null;
        }
    }

    isConnected(): boolean {
        return this.socket?.readyState === WebSocket.OPEN;

    }

    onMessageType(msgType: string): Observable<WebSocketMessage> {
        return this.message$.pipe(
            filter(message => {
                return message.type === msgType
            })
        );
    }

    subscribe(channel: string): void {
        this.send({
            type: 'subscribe',
            channel: channel
        });
    }

    unsubscribe(channel: string): void {
        this.send({
            type: 'unsubscribe',
            channel: channel
        });
    }

    send(message: any): void {
        if (this.isConnected()) {
            this.socket?.send(JSON.stringify(message));
        }
    }

    disconnect(): void {
        this.stopHeartBeat();
        this.reconnectionAttempts = this.maxAllowReconnectionsAttempts;

        if (this.socket) {
            this.socket.close();
            this.socket = null;
        }
        console.log("Websocket connection disconnected!", this.message$, "___", this.messageSubject)
    }

    ngOnDestroy(): void {
        this.disconnect();
    }


    // private socket$!: WebSocketSubject<any>;

    // constructor() { }

    // connect(): Observable<any> {
    //     if (!this.socket$ || this.socket$.closed) {
    //         try {
    //             this.socket$ = webSocket({
    //                 url: 'wss://ws.ifelse.io',
    //                 deserializer: e => {
    //                     try {
    //                         return JSON.parse(e.data);
    //                     } catch {
    //                         return e.data; // fallback to raw text
    //                     }
    //                 }
    //             }
    //             );
    //         } 
    //         catch (error) {
    //             console.log('WebSocket connection error:', error);
    //             Swal.fire({
    //                 icon: 'error',
    //                 title: 'WebSocket Connection Error',
    //                 text: error instanceof Error ? error?.message : 'An unknown error occurred.',
    //                 timer: 5000,
    //                 showConfirmButton: false,
    //                 toast: true,
    //                 position: "top-end",
    //             });
    //         }
    //     }

    //     return this.socket$.asObservable();
    // }

    // send(message: any) {
    //     this.socket$.next(JSON.stringify({ message: message }));
    // }

    // close() {
    //     this.socket$.complete();
    // }
}